<script setup lang="ts">
import { toRefs, Ref, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import {
	required,
	email,
	isTrustedDomain,
} from "@/ui/validation/validationRules";
import { useApiClient } from "@/api/client";
import ExportScheduleRecipientDO = App.Data.ExportScheduler.ExportScheduleRecipientDO;
import { toastApiResult } from "@/toaster";
import ExportScheduleDO = App.Data.ExportScheduler.ExportScheduleDO;
import Modal from "@/ui/components/modal.vue";
import { useBaseStore } from "@/stores/base";

const isDialogVisible = defineModel<boolean>();

const { t } = useI18n();
const props = defineProps<{
	allowedDomains: string[];
	exportSchedule: ExportScheduleDO;
}>();

const { allowedDomains, exportSchedule } = toRefs(props);
const recipientToSave = ref<ExportScheduleRecipientDO>({
	email: "",
});

const isFormValid: Ref<boolean> = ref(false);
const isLoading: Ref<boolean> = ref(false);

const resetForm = () => {
	recipientToSave.value.email = "";
	isFormValid.value = false;
};

const createRecipient = async () => {
	try {
		isLoading.value = true;
		const response = await toastApiResult(
			useApiClient("exportSchedules").createRecipient(
				exportSchedule.value,
				recipientToSave.value
			)
		);
		if (response.isOk()) {
			resetForm();
			isDialogVisible.value = false;
		}
	} finally {
		isLoading.value = false;
	}
};

const canSubmit = computed(
	() =>
		allowedDomains.value.length > 0 &&
		isFormValid.value !== false &&
		!isLoading.value
);

const canEditDomains = computed(() =>
	useBaseStore().currentUser.availablePermissions.includes(
		"can_manage_environment"
	)
);
</script>

<template>
	<Modal
		title="Add New Recipient"
		icon="fa-duotone fa-envelope-dot fa-xl"
		v-model:visible="isDialogVisible"
		@submit="createRecipient"
		v-model:allow-submit="canSubmit"
	>
		<v-form v-model="isFormValid" v-if="allowedDomains.length > 0">
			<v-text-field
				v-model="recipientToSave.email"
				:rules="[required(t), email(t), isTrustedDomain(t, allowedDomains)]"
				variant="outlined"
				:loading="isLoading"
				label="Email Address"
				placeholder="Enter recipient's email address"
				prepend-inner-icon="fa-regular fa-envelope"
				persistent-placeholder
				class="email-field"
				hint="Email must belong to one of the allowed domains"
				:disabled="isLoading"
				autocomplete="off"
			/>
		</v-form>

		<v-alert
			v-else
			density="comfortable"
			type="error"
			variant="outlined"
			title="No Allowed Domains Configured"
		>
			<p>
				The environment has no allowed domains configured. Allowed domains can
				be setup in the environment settings by an administrator.
			</p>
			<v-btn
				v-if="canEditDomains"
				color="primary"
				to="/environment/general-settings"
				class="mt-3"
				prepend-icon="fa-regular fa-gear"
			>
				Configure Domains
			</v-btn>
		</v-alert>
	</Modal>
</template>
